import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { moreAnimation } from 'src/app/animations/animations';
import { IEvent } from 'src/app/models/event.model';
import { ITicketType, ITicketTypeCategory } from 'src/app/models/ticket.model';
import { GetParams, TableOptions } from 'src/app/models/type.definition';
import { EventService, TicketService, TicketTypeCategoryService, TicketTypeService } from 'src/app/providers';
import { DateService } from 'src/app/services/date.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { NotificationService } from 'src/app/services/notification.service';
import { GetEvent } from '../../getEvent';

@Component({
  selector: 'app-event-ticketing',
  templateUrl: './event-ticketing.component.html',
  styleUrls: ['./event-ticketing.component.scss'],
  providers: [GetEvent],
  animations: [
    moreAnimation
  ]
})
export class EventTicketingComponent implements OnInit {
  @HostListener('document:click')
  documentClick() {
    this.createToggleOpen && (this.createToggleOpen = false);
  }
  private _subscription = new Subscription();
  public event: IEvent;
  public loaded: boolean;
  public createToggleOpen: boolean;
  public createToggle: { ref, links: { title: string, method: Function, class?: string }[] };

  public error: string;
  public loadingState$: Subject<void> = new Subject<void>();
  public currentUrl: string;
  public tableOptions: TableOptions[] = [
    {
      title: "Nom",
      class: "name",
      sort: true,
      key: 'name',
      //sortMethod: (order) => this.sort("name", order),
    },
    {
      title: "Prix TTC",
      class: "price",
      sort: false,
      key: 'stringPrice',

      //sortMethod: (order) => this.sort("dates.startDate", order)
    },
    {
      title: "Quota (en cours)",
      class: "quota",
      sort: false,
      key: 'totalQuota',
      //sortMethod: (order) => this.sort("ticketing.quota", order)
    },
    {
      title: "Disponibilité",
      class: "availability",
      sort: false,
      key: 'available',
      //sortMethod: (order) => this.sort("ticketing.totalPrice.sellingPrice", order),
    },
    {
      title: "En ligne",
      class: "active",
      sort: false,
      key: 'isVisible',
      toggleBox: true,
      callback: this.changeVisibility
      //sortMethod: (order) => this.sort("status", order)
    },
    {
      class: "more",
      toggle: [
        {
          title: 'Modifier',
          method: this.edit
        },
        {
          title: 'Supprimer',
          class: "red",
          if: (data: IEvent) => {
            if (!this.event.useRoomPlacement) {
              return true;
            } else {
              if (data) {
                if (data['eventId']) {
                  return false;
                }
              }
              return true;
            }
          },
          method: this.delete
        }
      ]
    }
  ];

  constructor(
    private getEvent: GetEvent,
    private eventService: EventService,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    private typeService: TicketTypeService,
    private ticketService: TicketService,
    private categoryService: TicketTypeCategoryService,
    private notification: NotificationService,
    private dateService: DateService
  ) { }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  async ngOnInit() {
    const options: GetParams<IEvent> = {}
    this.currentUrl = this.router.url;
    this.event = await this.getEvent.get(options);
    this.event.ticketTypeCategories = this.event.ticketTypeCategories.map(cat => {
      cat.ticketTypes = cat.ticketTypes.map((ticket: ITicketType) => {
        ticket.id = ticket._id;
        return ticket
      })
      return cat
    })
    if (this.event.bookType == "nft") {
      this.tableOptions[this.tableOptions.length - 1].toggle.unshift({
        title: 'Modifier l\'apparence',
        if: (data) => {
          return false;
        },
        method: this.editAppearance,
      });
    }
    if (this.event.useRoomPlacement) {
      this.createToggle = {
        ref: this,
        links: [
          {
            title: 'Nouveau ticket',
            method: this.createRate
          }
        ]
      };
    } else {
      this.createToggle = {
        ref: this,
        links: [
          {
            title: 'Nouveau ticket',
            method: this.createRate
          },
          {
            title: 'Nouvelle catégorie',
            method: this.createCategory
          }
        ]
      };
    }

    if (new Date(this.event.ticketing.date.endDate).getTime() > new Date().getTime()) {
      this.createToggle.links.push({
        title: 'Clôturer billetterie',
        class: "red",
        method: this.close
      })
    }
    this.loaded = true;

    this.event.ticketTypeCategories?.forEach(category => category.ticketTypes?.forEach(ticket => {
      ticket.price = ticket.price < 0 ? 0 : ticket.price;
      ticket.stringPrice = ticket.price ? (ticket.price / 100) + '€' : 'Gratuit';
      const startDate = ticket.scheduled?.startDate;
      const endDate = ticket.scheduled?.endDate;
      if (startDate && endDate) {
        ticket.available = `du ${this.dateService.formatDateAndTime(startDate)} au ${this.dateService.formatDateAndTime(endDate)}`;
      }
      else if (startDate) {
        ticket.available = `à partir du ${this.dateService.formatDateAndTime(startDate)}`;
      }
      else if (endDate) {
        ticket.available = `jusqu'au ${this.dateService.formatDateAndTime(endDate)}`;
      }
      else {
        ticket.available = 'toujours';
      }
      // await resetOrders(this.event, this.categoryService, this.typeService);
    }));
    await this.getTicketSells();
    this.event.ticketTypeCategories.sort((a, b) => a.order > b.order ? 1 : -1)
  }

  async getTicketSells(): Promise<void> {
    const soldCounts = await this.ticketService.getSoldCounts(this.event._id).toPromise();
    const soldTicketIds = soldCounts.map(_ => _.id);
    soldCounts.forEach(soldTicket => {
      const ticket = this.getTicket(soldTicket.id);
      if (ticket) {
        if (ticket.importId) {
          ticket.name = `[Import ${ticket.importId.provider}] - ${ticket.name}`
        }
        ticket.sold = ticket.stocks.sold;
        ticket.totalQuota = `${ticket.stocks.sold}/${ticket.stocks.quota || 'ꝏ'} (${ticket.stocks.inDraft || 0})`;
      }
    });
    this.event.ticketTypeCategories.forEach(cat => {
      cat.ticketTypes.forEach(ticket => {
        if (!soldTicketIds.includes(ticket._id)) {
          ticket.totalQuota = `${ticket.stocks.sold}/${ticket.stocks.quota || 'ꝏ'} (${ticket.stocks.inDraft || 0})`;
        }
      })
    })
    this.calcSoldByCategory()
  }

  getTicket(id: string): ITicketType {
    for (const category of this.event.ticketTypeCategories) {
      for (const ticketType of category.ticketTypes) {
        if (id == ticketType._id) {
          return ticketType;
        }
      }
    }
  }

  calcSoldByCategory(): void {
    this.event.ticketTypeCategories.forEach(category => {
      category.totalQuota = ``;
      if (category._id)  {
        category.totalQuota = `${category.stocks.sold}/${category.stocks.quota || 'ꝏ'}`;
      }
    })
  }

  displayCreateToggle() {
    setTimeout(() => this.createToggleOpen = !this.createToggleOpen);
  }

  createRate(ref = this) {
    ref.router.navigate([`${ref.currentUrl}/creer-ticket`])
  }

  createCategory(ref = this) {
    ref.router.navigate([`${ref.currentUrl}/creer-categorie`])
  }

  edit(element: ITicketTypeCategory | ITicketType, ref = this): void {
    const keyword = (element as ITicketType).stringPrice ? 'ticket' : 'categorie';
    ref.router.navigateByUrl(`${ref.currentUrl}/editer-${keyword}/${element._id}`);
  }

  editAppearance(element: ITicketTypeCategory | ITicketType, ref = this): void {
    ref.router.navigateByUrl(`${ref.currentUrl}/e-tickets/${element._id}`);
  }

  delete(element: ITicketTypeCategory | ITicketType, ref = this): void {
    const type = (element as ITicketType).stringPrice ? 'Tarif' : 'Catégorie';
    const service = type == 'Tarif' ? ref.typeService : ref.categoryService;
    ref._subscription.add(service.delete(element._id).subscribe(() => {
      ref.notification.newNotification({ message: `${type} supprimé avec succés`, state: 'success' });
      if (type == 'Catégorie') {
        const catIndex = ref.event.ticketTypeCategories.findIndex(_ => _._id == element._id);
        ref.event.ticketTypeCategories.splice(catIndex, 1)[0];
      }
      else {
        const catIndex = ref.event.ticketTypeCategories.findIndex(_ => _._id == (element as ITicketType).ticketTypeCategoryId);
        const ticketIndex = ref.event.ticketTypeCategories[catIndex].ticketTypes.findIndex(_ => _._id == element._id);
        ref.event.ticketTypeCategories[catIndex].ticketTypes.splice(ticketIndex, 1);
        if (!ref.event.ticketTypeCategories[catIndex]._id && !ref.event.ticketTypeCategories[catIndex].ticketTypes.length) {
          ref.event.ticketTypeCategories.pop();
        }
      }
    }, err => {
      ref.notification.newNotification({ message: ref.errorHandler.getError(err), state: 'error' });
    })
    );
  }

  ticketDragged(event: { id: string, order: number, parentId: string }) {

    const updateObject: ITicketType = {
      order: event.order,
      ticketTypeCategoryId: { _id: event.parentId }
    }

    if (!event.parentId) {
      updateObject.ticketTypeCategoryId = null
    }

    if (!event.id) {
      delete updateObject.ticketTypeCategoryId;
    }
    const observable = event.id ? this.typeService.update(event.id, updateObject) : this.categoryService.update(event.parentId, updateObject)
    observable.subscribe(res => { this.calcSoldByCategory() }, err => {
      this.notification.newNotification({ message: "Une erreur est survenue", state: "error" });
    })
  }

  public async changeVisibility(data: ITicketType | ITicketTypeCategory, ref = this): Promise<void> {
    const provider = 'ticketTypeCategoryId' in (data as ITicketType) ? ref.typeService : ref.categoryService
    try {
      if (!data.isVisible && 'importId' in data && data.importId?._id) {
        ref.notification.newNotification({ message: "Les tarifs utilisés pour les imports ne peuvent être visibles", state: "error" });
      }
      data.isVisible = !data.isVisible;
      await provider.update(data._id, {
        isVisible: data.isVisible,
      }).toPromise()
    }
    catch (err) {
      console.error(err);
    }
  }

  public async close(ref = this): Promise<void> {
    const date = new Date;
    date.setMinutes(date.getMinutes() - 1);
    ref.event.ticketing.date.endDate = date;
    ref.createToggleOpen = false;
    await ref.eventService.update(ref.event._id, ref.event).toPromise();
    ref.notification.newNotification({ message: "Billetterie clôturer avec succès", state: "success" });
    ref.createToggle.links.splice(2, 1);
  }


}
